/**
 * Settings → Connectors: how senders such as the Boomi connector reach the vaults. The vaults show the address
 * connectors send to (vault-reported, or set here by hand); connector keys are the credential a sender uses. A new key's
 * secret is shown once, in a panel under the title, and lives only in this component's state.
 */
import { useState, type FormEvent } from 'react';
import { useModalSteps } from '../lib/urlmodal.ts';
import { useSearchParams } from '../lib/liveparams.ts';
import { useAuth } from '../lib/auth.tsx';
import { Api, ApiError, type ConnectorKey, type Environment, type KeyScope, type VaultInfo } from '../lib/api.ts';
import { useAsync } from '../lib/hooks.ts';
import { dateTime } from '../lib/format.ts';
import { ingestSource, lastUsed, parseIngestUrl, scopeCheck, scopeEmpty, scopeLine, sendsToLine } from '../lib/connectors.ts';
import { IconButton, Kicker, Surface, cx } from '../design/primitives.tsx';
import { AlertIcon, CheckIcon, PencilIcon, PlusIcon, TrashIcon } from '../components/icons.tsx';
import { EnvLabel, EnvSelect } from '../components/EnvBits.tsx';
import { useEnv } from '../lib/envs.tsx';
import { useFormDraft, useUrlParam } from '../lib/formstate.ts';
import { clearForm } from '../lib/pagedraft.ts';
import { flagOn, withFlag, withParam } from '../lib/panelview.ts';
import { Button, Field, Spinner, inputClass, useToast } from '../components/ui.tsx';
import { Confirm, Copyable, Panel, Title } from './Settings.tsx';

const message = (e: unknown) => (e instanceof ApiError ? e.message : String(e));

export function ConnectorsSettings() {
  const { tenant } = useAuth();
  const tid = tenant?.id ?? '';
  return (
    <>
      <Title>Connectors</Title>
      <p className="m-0 -mt-2 mb-6 text-small text-n8 max-w-3xl">
        Senders such as the Boomi connector pick a vault when you import an operation and then send straight to it. A connector key is the credential they use; the address below is where they find each vault.
      </p>
      <div className="flex flex-col gap-10">
        <VaultAddresses tid={tid} />
        <Keys tid={tid} />
      </div>
    </>
  );
}

function SectionTitle({ children, action }: { children: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <h2 className="m-0 text-body font-semibold">{children}</h2>
      {action}
    </div>
  );
}

// ---------------------------------------------------------------- vault addresses

function VaultAddresses({ tid }: { tid: string }) {
  const { envs } = useEnv();
  // Every environment's vaults, labelled: a key reaches only those of its own environment
  const vaults = useAsync(() => Api.vaults(tid, 'all'), [tid], 10_000);
  const pos = (n: VaultInfo) => envs.find((e) => e.id === n.environmentId)?.position ?? 0;
  const active = (vaults.data ?? []).filter((n) => !n.revokedAt).sort((a, b) => pos(a) - pos(b) || a.name.localeCompare(b.name));
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Vault addresses</SectionTitle>
      {vaults.error && <p className="m-0 text-small text-fail">{vaults.error}</p>}
      {vaults.loading && !vaults.data && <Spinner />}
      {vaults.data && active.length === 0 && <Surface className="p-8 text-center text-small text-n8">No vaults yet. Install one under Vaults.</Surface>}
      {active.length > 0 && (
        <Surface className="px-4 py-1">
          {active.map((n, i) => <VaultRow key={n.id} vault={n} tid={tid} first={i === 0} env={envs.find((e) => e.id === n.environmentId)} showEnv={envs.length > 1} onSaved={vaults.reload} />)}
        </Surface>
      )}
    </section>
  );
}

function VaultRow({ vault: n, tid, first, env, showEnv, onSaved }: { vault: VaultInfo; tid: string; first: boolean; env: Environment | undefined; showEnv: boolean; onSaved: () => void }) {
  return (
    <div className={cx('py-3.5 flex flex-col gap-2', !first && 'border-t border-n4')}>
      <div className="flex items-center gap-2.5 flex-wrap">
        <span className={cx('size-2 rounded-full shrink-0', n.online ? 'bg-ok' : 'bg-n6')} role="img" aria-label={n.online ? 'Online' : 'Offline'} />
        <span className="text-body font-medium">{n.name}</span>
        <span className="text-micro text-n7">{n.version ?? ''}</span>
        {showEnv && <span className="text-micro text-n8 rounded-full bg-n3 h-6 px-2.5 inline-flex items-center"><EnvLabel env={env} /></span>}
      </div>
      <IngestAddress vault={n} tid={tid} onSaved={onSaved} className="pl-[18px]" />
    </div>
  );
}

/** The address connectors send to for one vault, with its editor (owners): shared by this page and the vault's own page. */
export function IngestAddress({ vault: n, tid, onSaved, className }: { vault: VaultInfo; tid: string; onSaved: () => void; className?: string }) {
  const { canManage } = useAuth();
  const toast = useToast();
  // The editor is open while the URL names this vault (replace); the typed address is a draft in sessionStorage
  const [editingId, setEditingId] = useUrlParam('ingest');
  const editing = editingId === n.id;
  const draftKey = `ingest.${n.id}`;
  const [{ value }, setDraft, clearDraft] = useFormDraft(draftKey, { value: n.ingestUrlOverride ?? n.ingestUrl ?? '' });
  const setValue = (v: string) => setDraft({ value: v });
  const setEditing = (on: boolean) => { if (!on) clearDraft(); setEditingId(on ? n.id : undefined); };
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const source = ingestSource(n);

  const open = () => { clearDraft(); setError(undefined); setEditing(true); };
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const r = parseIngestUrl(value);
    if ('error' in r) return setError(r.error);
    setBusy(true);
    try {
      await Api.setVaultIngestUrl(tid, n.id, r.url);
      toast(r.url ? 'Address saved' : 'Manual address cleared');
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <form onSubmit={(e) => void save(e)} className={cx('flex flex-col gap-2', className)}>
        <Field label="Address connectors use" error={error} hint={n.reportedIngestUrl ? `The vault reports ${n.reportedIngestUrl}. Leave empty to use it.` : 'Leave empty to clear a manual address.'}>
          <input className={cx(inputClass, 'font-mono max-w-xl')} value={value} autoFocus placeholder="https://vault1.example.com:8080" onChange={(e) => { setValue(e.target.value); setError(undefined); }} />
        </Field>
        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={busy}>Save</Button>
          <Button type="button" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      </form>
    );
  }
  return (
    <div className={cx('flex items-baseline gap-x-3 gap-y-1 flex-wrap', className)}>
      {source === 'none' ? (
        <p className="m-0 text-small text-n8">No address yet. Set <code className="font-mono text-micro">ingestUrl</code> in the vault's vault.json{canManage ? ' or enter an address' : ''}.</p>
      ) : (
        <>
          <code className="font-mono text-small text-n10 [overflow-wrap:anywhere]">{n.ingestUrl}</code>
          <span className="text-micro text-n7">{source === 'override' ? 'set manually' : 'reported by the vault'}</span>
          {source === 'override' && n.reportedIngestUrl && <span className="text-micro text-n7">the vault reports <code className="font-mono">{n.reportedIngestUrl}</code></span>}
        </>
      )}
      {canManage && <Button variant="ghost" className="ml-auto h-8" onClick={open}>{source === 'none' ? 'Enter an address' : 'Edit'}</Button>}
    </div>
  );
}

// ---------------------------------------------------------------- connector keys

type Created = { keyId: string; secret: string; name: string; environmentId: string; scope: KeyScope };

function Keys({ tid }: { tid: string }) {
  const { canManage } = useAuth();
  const toast = useToast();
  const { envs, current } = useEnv();
  const envOf = (id: string) => envs.find((e) => e.id === id);
  const keys = useAsync(() => Api.connectorKeys(tid), [tid], 15_000);
  // Which panel is open is in the URL: `new=1` the create panel, `kedit=<keyId>` the editor of that key. Opening one is a
  // history step (Back closes it), another one over it a step too, and closing goes back past them (lib/urlmodal.ts)
  const [params] = useSearchParams();
  const creating = flagOn(params, 'new');
  const editing = params.get('kedit') ?? undefined;
  const panelWin = useModalSteps('keyPanelOpened');
  const show = (p: { creating?: boolean; editing?: string }) => {
    const write = (q: URLSearchParams) => withParam(withFlag(q, 'new', !!p.creating), 'kedit', p.editing);
    if (!p.creating && !p.editing) panelWin.close(write);
    else if (panelWin.opened) panelWin.step(write);
    else panelWin.open(write);
  };
  const [created, setCreated] = useState<Created>();
  const [revoking, setRevoking] = useState<{ keyId: string; name: string }>();
  const list = keys.data ?? [];

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle action={canManage && <Button variant="primary" aria-expanded={creating} onClick={() => { if (creating) clearForm(`connkey.new.${tid}`); show({ creating: !creating }); setCreated(undefined); }}><PlusIcon size={16} />Create key</Button>}>Connector keys</SectionTitle>
      {creating && !created && <CreatePanel tid={tid} envs={envs} initialEnv={current?.id ?? ''} onClose={() => show({})} onCreated={(k) => { setCreated(k); keys.reload(); }} />}
      {created && <Reveal k={created} env={envOf(created.environmentId)} onClose={() => { setCreated(undefined); show({}); }} />}
      {revoking && (
        <Confirm question={`Revoke key ${revoking.name}?`} action="Revoke" onCancel={() => setRevoking(undefined)} onConfirm={async () => {
          try {
            await Api.revokeConnectorKey(tid, revoking.keyId);
            toast(`Key ${revoking.name} revoked`);
            keys.reload();
          } catch (e) {
            toast(message(e), 'fail');
          }
          setRevoking(undefined);
        }}>
          Senders using it can no longer send or list vaults. The vaults stop accepting it at once.
        </Confirm>
      )}
      {keys.error && <p className="m-0 text-small text-fail">{keys.error}</p>}
      {keys.loading && !keys.data && <Spinner />}
      {keys.data && list.length === 0 && <Surface className="p-8 text-center text-small text-n8">No keys yet. Create one for each sender.</Surface>}
      {list.length > 0 && (
        <Surface className="px-4 py-1">
          {list.map((k, i) => {
            const env = envOf(k.environmentId);
            if (editing === k.keyId) {
              return (
                <div key={k.keyId} className={cx('py-4', i > 0 && 'border-t border-n4')}>
                  <EditPanel tid={tid} k={k} env={env} onClose={() => show({})} onSaved={() => { show({}); keys.reload(); }} />
                </div>
              );
            }
            const empty = scopeEmpty(k.scope);
            return (
              <div key={k.keyId} className={cx('py-3 flex items-center gap-x-4 gap-y-2 flex-wrap', i > 0 && 'border-t border-n4')}>
                <div className="flex-1 min-w-[220px] flex flex-col gap-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-small font-medium">{k.name}</span>
                    <span className="text-micro text-n8 rounded-full bg-n3 h-6 px-2.5 inline-flex items-center"><EnvLabel env={env} /></span>
                  </div>
                  <code className="font-mono text-micro text-n7 [overflow-wrap:anywhere]">{k.keyId}</code>
                  <span className={cx('text-small flex items-start gap-1.5', empty ? 'text-warn' : 'text-n9')}>
                    {empty && <AlertIcon size={14} className="mt-0.5 shrink-0" />}
                    <span>{empty ? <b className="font-medium">Warning: </b> : <span className="text-n7">Sends to </span>}{scopeLine(k.scope, env?.name ?? 'its environment')}</span>
                  </span>
                </div>
                <div className="text-micro text-n7 flex flex-col gap-0.5 sm:text-right">
                  <span>created {dateTime(k.createdAt)}</span>
                  <span>{k.lastUsedAt ? `used ${lastUsed(k.lastUsedAt)}` : 'not used yet'}</span>
                </div>
                {canManage && (
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" className="h-8" aria-label={`Edit ${k.name}`} onClick={() => { show({ editing: k.keyId }); setCreated(undefined); }}><PencilIcon size={14} />Edit</Button>
                    <IconButton label={`Revoke ${k.name}`} onClick={() => setRevoking(k)}><TrashIcon size={16} /></IconButton>
                  </div>
                )}
              </div>
            );
          })}
        </Surface>
      )}
    </section>
  );
}

/** The vaults of one environment (active ones), polled so a vault that comes online shows up. */
function useEnvVaults(tid: string, envId: string) {
  const list = useAsync(() => Api.vaults(tid, 'all'), [tid], 10_000);
  const vaults = (list.data ?? []).filter((n) => !n.revokedAt && n.environmentId === envId).sort((a, b) => a.name.localeCompare(b.name));
  return { vaults, loading: list.loading && !list.data, error: list.error };
}

/** The scope choice of a key: all vaults of its environment, or a checked list of them. Shared by creating and editing. */
function ScopePicker({ tid, envId, envName, mode, selected, onMode, onSelected, idPrefix }: {
  tid: string; envId: string; envName: string; mode: 'environment' | 'vaults'; selected: string[];
  onMode: (m: 'environment' | 'vaults') => void; onSelected: (ids: string[]) => void; idPrefix: string;
}) {
  const { vaults, loading, error } = useEnvVaults(tid, envId);
  const check = scopeCheck(mode, selected.filter((id) => vaults.some((v) => v.id === id)).length, vaults.length);
  const toggle = (id: string) => onSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const radio = 'accent-[var(--accent)] size-4 mt-0.5 shrink-0';
  return (
    <fieldset className="m-0 p-0 border-0 flex flex-col gap-2 min-w-0">
      <legend className="text-small font-medium p-0 mb-2">Scope</legend>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input type="radio" name={`${idPrefix}-scope`} className={radio} checked={mode === 'environment'} onChange={() => onMode('environment')} />
        <span className="flex flex-col"><span className="text-small">All vaults in {envName}</span><span className="text-micro text-n7">Also vaults added later</span></span>
      </label>
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input type="radio" name={`${idPrefix}-scope`} className={radio} checked={mode === 'vaults'} onChange={() => onMode('vaults')} />
        <span className="flex flex-col"><span className="text-small">Only selected vaults</span></span>
      </label>
      {mode === 'vaults' && (
        <div className="ml-[26px] flex flex-col gap-1 max-w-xl">
          {loading && <Spinner />}
          {error && <p className="m-0 text-small text-fail">{error}</p>}
          {!loading && vaults.length === 0 && !error && <p className="m-0 text-small text-n8">No vaults in {envName} yet.</p>}
          {vaults.map((v) => (
            <label key={v.id} className="flex items-center gap-2.5 min-h-9 px-2 -mx-2 rounded-chip cursor-pointer hover:bg-n3/60">
              <input type="checkbox" className="accent-[var(--accent)] size-4 shrink-0" checked={selected.includes(v.id)} onChange={() => toggle(v.id)} />
              <span className={cx('size-2 rounded-full shrink-0', v.online ? 'bg-ok' : 'bg-n6')} role="img" aria-label={v.online ? 'Online' : 'Offline'} />
              <span className="text-small">{v.name}</span>
              <span className="text-micro text-n7 truncate">{v.host?.hostname ?? v.hostname ?? ''}</span>
            </label>
          ))}
        </div>
      )}
      {!check.ok && <p className="m-0 ml-[26px] text-small text-warn flex items-center gap-1.5" role="status"><AlertIcon size={14} className="shrink-0" />{check.hint}</p>}
    </fieldset>
  );
}

/** Whether the picked vaults can be sent: the ones still in the environment, and the hint when not. */
function useScopeCheck(tid: string, envId: string, mode: 'environment' | 'vaults', selected: string[]) {
  const { vaults } = useEnvVaults(tid, envId);
  return scopeCheck(mode, selected.filter((id) => vaults.some((v) => v.id === id)).length, vaults.length);
}

function CreatePanel({ tid, envs, initialEnv, onClose, onCreated }: {
  tid: string; envs: Environment[]; initialEnv: string; onClose: () => void; onCreated: (k: Created) => void;
}) {
  // Typed name, environment and vault picks are a draft in sessionStorage (Back returns to the filled form); the secret never exists before the key is made
  const [d, setDraft, clearDraft] = useFormDraft(`connkey.new.${tid}`, { name: '', envId: initialEnv, mode: 'environment' as string, selected: [] as string[] });
  const { name, selected } = d;
  const envId = envs.some((e) => e.id === d.envId) ? d.envId : initialEnv;
  const mode: 'environment' | 'vaults' = d.mode === 'vaults' ? 'vaults' : 'environment';
  const setName = (v: string) => setDraft({ name: v });
  const setEnvId = (v: string) => setDraft({ envId: v, selected: [] });
  const setMode = (m: 'environment' | 'vaults') => setDraft({ mode: m });
  const setSelected = (ids: string[]) => setDraft({ selected: ids });
  const close = () => { clearDraft(); onClose(); };
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const env = envs.find((e) => e.id === envId);
  const check = useScopeCheck(tid, envId, mode, selected);
  const { vaults } = useEnvVaults(tid, envId);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!check.ok) return;
    setBusy(true);
    setError(undefined);
    try {
      const ids = mode === 'vaults' ? selected.filter((id) => vaults.some((v) => v.id === id)) : null;
      const key = await Api.createConnectorKey(tid, name.trim() || 'connector key', envId || undefined, ids);
      clearDraft();
      onCreated(key);
    } catch (err) {
      setError(message(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title="Create a connector key" onClose={close}>
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-5">
        <div className="flex items-start gap-3 flex-wrap">
          <Field label="Name" hint="Which sender uses it, for example Boomi production." className="w-full max-w-sm">
            <input className={inputClass} value={name} autoFocus maxLength={100} placeholder="Boomi production" onChange={(e) => setName(e.target.value)} />
          </Field>
          {envs.length > 1 && (
            <Field label="Environment" hint="The key sends to, and lists, the vaults of this environment." className="w-full sm:w-56">
              <EnvSelect envs={envs} value={envId} onChange={setEnvId} />
            </Field>
          )}
        </div>
        <ScopePicker tid={tid} envId={envId} envName={env?.name ?? 'this environment'} mode={mode} selected={selected} onMode={setMode} onSelected={setSelected} idPrefix="create" />
        <div className="flex items-center gap-3 flex-wrap">
          <Button type="submit" variant="primary" disabled={busy || !check.ok} title={check.hint}>Create key</Button>
          {error && <span className="text-small text-fail">{error}</span>}
        </div>
      </form>
    </Panel>
  );
}

/** Rename a key and change what it sends to (the environment is fixed). */
function EditPanel({ tid, k, env, onClose, onSaved }: { tid: string; k: ConnectorKey; env: Environment | undefined; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const [d, setDraft, clearDraft] = useFormDraft(`connkey.edit.${k.keyId}`, { name: k.name, mode: k.scope.kind as string, selected: k.scope.vaults.map((v) => v.id) });
  const { name, selected } = d;
  const mode: 'environment' | 'vaults' = d.mode === 'vaults' ? 'vaults' : 'environment';
  const setName = (v: string) => setDraft({ name: v });
  const setMode = (m: 'environment' | 'vaults') => setDraft({ mode: m });
  const setSelected = (ids: string[]) => setDraft({ selected: ids });
  const close = () => { clearDraft(); onClose(); };
  const [busy, setBusy] = useState(false);
  const check = useScopeCheck(tid, k.environmentId, mode, selected);
  const { vaults } = useEnvVaults(tid, k.environmentId);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!check.ok) return;
    setBusy(true);
    try {
      const ids = mode === 'vaults' ? selected.filter((id) => vaults.some((v) => v.id === id)) : null;
      await Api.updateConnectorKey(tid, k.keyId, { name: name.trim() || k.name, vaultIds: ids });
      toast(`Key ${name.trim() || k.name} saved`);
      clearDraft();
      onSaved();
    } catch (err) {
      toast(message(err), 'fail');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Panel title={`Edit key ${k.name}`} onClose={close}>
      <form onSubmit={(e) => void save(e)} className="flex flex-col gap-5">
        <div className="flex items-start gap-3 flex-wrap">
          <Field label="Name" className="w-full max-w-sm">
            <input className={inputClass} value={name} autoFocus maxLength={100} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Environment" hint="Fixed: create a new key to use another environment." className="w-full sm:w-56">
            <div className="h-9 flex items-center text-small text-n9"><EnvLabel env={env} /></div>
          </Field>
        </div>
        <ScopePicker tid={tid} envId={k.environmentId} envName={env?.name ?? 'this environment'} mode={mode} selected={selected} onMode={setMode} onSelected={setSelected} idPrefix={`edit-${k.keyId}`} />
        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={busy || !check.ok} title={check.hint}>Save</Button>
          <Button type="button" variant="ghost" onClick={close}>Cancel</Button>
        </div>
      </form>
    </Panel>
  );
}

/** The secret, once. It exists only in this component's state: not in storage, not in the URL. */
function Reveal({ k, env, onClose }: { k: Created; env: Environment | undefined; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Panel title={`Key ${k.name} created`} onClose={onClose}>
      <p className="m-0 flex items-center gap-2 text-small text-n9">
        <span className="size-2 rounded-full bg-warn shrink-0" aria-hidden="true" />
        The secret is shown only once. Copy it now; it cannot be shown again.
      </p>
      <p className="m-0 text-small text-n9">{sendsToLine(k.scope, env?.name ?? 'its environment')}.</p>
      <div className="flex flex-col gap-3">
        <div><Kicker className="mb-1">Key id</Kicker><Copyable text={k.keyId} /></div>
        <div><Kicker className="mb-1">Key secret</Kicker><Copyable text={k.secret} /></div>
      </div>
      <p className="m-0 text-small text-n8 max-w-2xl">
        In the Boomi connection set Connection mode to <b className="font-medium text-n10">MessageVault cloud</b>, Cloud URL to <code className="font-mono text-micro text-n10">{window.location.origin}</code>, and paste the key id and the key secret into Key id and Key secret.
      </p>
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => { setCopied(true); onClose(); }}>{copied ? <CheckIcon size={16} /> : null}I have copied it</Button>
      </div>
    </Panel>
  );
}
